import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/video-intel';
const AUDIO_VERSION = 'v5';

/** Hardcoded demo file representing a sample training video */
const DEMO_FILE = {
  fileName: 'demo_training.mp4',
  fileSize: 52400000,
  uploadDate: '2026-02-23T10:00:00Z',
  s3Key: 'users/demo/demo_training.mp4',
  contentType: 'video/mp4',
};

/** Mock schema for video transcription data */
const DEMO_SCHEMA = [
  { name: 'clip_id', data_type: 'INT64' },
  { name: 'speaker', data_type: 'BYTE_ARRAY' },
  { name: 'transcript', data_type: 'BYTE_ARRAY' },
  { name: 'timestamp', data_type: 'BYTE_ARRAY' },
  { name: 'topic', data_type: 'BYTE_ARRAY' },
  { name: 'duration', data_type: 'INT64' },
  { name: 'sentiment', data_type: 'BYTE_ARRAY' },
  { name: 'video_title', data_type: 'BYTE_ARRAY' },
];

/** Mock rows of transcribed video data */
const DEMO_ROWS = [
  { clip_id: 2001, speaker: 'Priya Sharma', transcript: 'Welcome to the Q1 product demo. Today I\'ll walk you through the three major features we shipped this quarter...', timestamp: '00:00:12', topic: 'Product Demo', duration: 285, sentiment: 'positive', video_title: 'Q1 Product Demo Webinar' },
  { clip_id: 2002, speaker: 'Marcus Lee', transcript: 'Let me explain the new onboarding flow. We reduced the steps from seven to three, which cut drop-off by forty percent...', timestamp: '00:04:57', topic: 'Onboarding', duration: 198, sentiment: 'positive', video_title: 'Q1 Product Demo Webinar' },
  { clip_id: 2003, speaker: 'Rachel Torres', transcript: 'During the interview, the candidate struggled with system design questions. Needs more preparation on distributed systems...', timestamp: '00:00:45', topic: 'Interview Review', duration: 412, sentiment: 'neutral', video_title: 'Engineering Interview - Feb 19' },
  { clip_id: 2004, speaker: 'James Park', transcript: 'The compliance training module covers data handling procedures. All employees must complete this by end of month...', timestamp: '00:02:30', topic: 'Compliance Training', duration: 540, sentiment: 'neutral', video_title: 'Data Compliance Training 2026' },
  { clip_id: 2005, speaker: 'Priya Sharma', transcript: 'Customer feedback has been overwhelmingly positive on the dashboard redesign. Net promoter score jumped from 42 to 67...', timestamp: '00:12:18', topic: 'Customer Feedback', duration: 165, sentiment: 'positive', video_title: 'Q1 Product Demo Webinar' },
  { clip_id: 2006, speaker: 'Alex Nguyen', transcript: 'I have concerns about the timeline for the API migration. We\'re two weeks behind and the integration tests are failing...', timestamp: '00:08:44', topic: 'Project Status', duration: 320, sentiment: 'negative', video_title: 'Engineering Standup - Feb 20' },
];

/** Mock search results for "product demo" query */
const DEMO_SEARCH_RESULTS = [
  { clip_id: 2001, speaker: 'Priya Sharma', transcript: 'Welcome to the Q1 product demo. Today I\'ll walk you through the three major features...', sentiment: 'positive', topic: 'Product Demo' },
  { clip_id: 2002, speaker: 'Marcus Lee', transcript: 'Let me explain the new onboarding flow during this product demo session...', sentiment: 'positive', topic: 'Onboarding' },
  { clip_id: 2005, speaker: 'Priya Sharma', transcript: 'Customer feedback on the product demo has been overwhelmingly positive...', sentiment: 'positive', topic: 'Customer Feedback' },
];

/**
 * Video Intelligence demo script with LIVE DATA injection.
 * Uses hardcoded demo_training.mp4 with mock video transcript data
 * flowing through all 5 nodes of the video-intel flow template.
 */
export const videoIntelScript: DemoScript = {
  id: 'video-intel',
  templateId: 'video-intel',
  name: 'Video Intelligence Demo',
  description: 'Learn how to transcribe, browse, and search video recordings.',
  steps: [
    // === INTRO (no target) ===
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: 'Let me show you how BigSplice handles video intelligence \u2014 from raw video files to searchable transcripts in minutes.',
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 7000 },
    },

    // === STEP 1: Upload Video ===
    {
      id: 'upload-highlight',
      type: 'highlight',
      target: { nodeId: 'upload' },
      delayBefore: 200,
      subtitle: 'Start by uploading your video files. We support MP4, MOV, WebM, and other common formats \u2014 training recordings, webinars, interviews, you name it.',
      audio: { src: `${AUDIO_BASE}/upload-highlight.mp3?${AUDIO_VERSION}`, duration: 12000 },
      cursorPath: [
        { x: -80, y: -40, duration: 600 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'upload-sample-note',
      type: 'narrate',
      target: { nodeId: 'upload' },
      subtitle: 'Heads up \u2014 the data in this demo is simulated so we can move through the pipeline quickly. Once you upload a real video, BigSplice will actually transcribe and index it.',
      audio: { src: `${AUDIO_BASE}/upload-sample-note.mp3?${AUDIO_VERSION}`, duration: 10000 },
    },
    {
      id: 'upload-decision',
      type: 'decision',
      target: { nodeId: 'upload' },
      subtitle: 'Want me to use a sample training video, or would you like to upload your own?',
      audio: { src: `${AUDIO_BASE}/upload-decision.mp3?${AUDIO_VERSION}`, duration: 3000 },
      decision: {
        prompt: 'Want me to use a sample training video, or would you like to upload your own?',
        options: [
          { label: 'Use sample video', icon: 'video', nextStepId: 'upload-inject-uploading' },
          { label: 'I\u2019ll upload mine', icon: 'upload', nextStepId: 'upload-interact' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'upload-interact',
      type: 'interact',
      target: { nodeId: 'upload' },
      subtitle: 'Go ahead and drag a video file onto the upload area. I\'ll wait for you.',
      audio: { src: `${AUDIO_BASE}/upload-interact.mp3?${AUDIO_VERSION}`, duration: 4500 },
      autoAdvanceAfter: 4000,
    },

    // === INJECT: Simulate file upload progress ===
    {
      id: 'upload-inject-uploading',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Uploading file\u2026',
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_training.mp4', progress: 35 } },
      },
      autoAdvanceAfter: 250,
    },
    {
      id: 'upload-inject-progress',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_training.mp4', progress: 72 } },
      },
      autoAdvanceAfter: 200,
    },
    {
      id: 'upload-inject-almost',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_training.mp4', progress: 95 } },
      },
      autoAdvanceAfter: 150,
    },
    {
      id: 'upload-inject-done',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Upload complete.',
      audio: { src: `${AUDIO_BASE}/upload-inject.mp3?${AUDIO_VERSION}`, duration: 2500 },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'done', fileName: 'demo_training.mp4' } },
      },
    },

    // === STEP 2: Transcribe Video (with live status animation) ===
    {
      id: 'transcribe-inject-processing',
      type: 'inject',
      target: { nodeId: 'auto-process' },
      delayBefore: 150,
      subtitle: 'BigSplice extracts the audio track and transcribes your videos at up to 160x real-time speed per GPU. Training videos, webinars, recorded interviews \u2014 all converted to structured, searchable text automatically.',
      audio: { src: `${AUDIO_BASE}/transcribe-highlight.mp3?${AUDIO_VERSION}`, duration: 17000 },
      inject: {
        nodeId: 'auto-process',
        data: { demoState: { status: 'processing' } },
      },
      cursorPath: [
        { x: -60, y: 0, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    {
      id: 'transcribe-inject-complete',
      type: 'inject',
      target: { nodeId: 'auto-process' },
      subtitle: 'Transcription complete. Your video content is now structured data.',
      audio: { src: `${AUDIO_BASE}/transcribe-wait.mp3?${AUDIO_VERSION}`, duration: 6500 },
      inject: {
        nodeId: 'auto-process',
        data: { demoState: { status: 'complete' } },
      },
    },

    // === STEP 3: View Data ===
    {
      id: 'dataset-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      subtitle: 'Now let\u2019s view the data and explore the transcription results.',
      audio: { src: `${AUDIO_BASE}/dataset-highlight.mp3?${AUDIO_VERSION}`, duration: 3000 },
      cursorPath: [
        { x: -40, y: -30, duration: 400 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    // Pre-inject mock data into downstream nodes BEFORE file selection triggers them
    {
      id: 'sheets-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'sheets',
        data: {
          demoState: {
            schema: DEMO_SCHEMA,
            rows: DEMO_ROWS,
            rowCount: 1243,
          },
        },
      },
      autoAdvanceAfter: 100,
    },
    {
      id: 'search-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'search',
        data: {
          demoState: {},
        },
      },
      autoAdvanceAfter: 100,
    },
    // Now inject the file — downstream nodes already have demoState and won't call APIs
    {
      id: 'dataset-inject',
      type: 'inject',
      target: { nodeId: 'sheets' },
      subtitle: 'Loading data\u2026 Watch how the data flows through the pipeline.',
      audio: { src: `${AUDIO_BASE}/dataset-selected.mp3?${AUDIO_VERSION}`, duration: 5000 },
      inject: {
        nodeId: 'sheets',
        data: { selectedFile: DEMO_FILE },
      },
    },

    // === STEP 4: Browse Data (mock data already loaded via demoState) ===
    {
      id: 'sheets-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 400,
      subtitle: 'Here\'s your transcribed video data in a spreadsheet. Each row is a clip \u2014 with the speaker, transcript, timestamp, topic, and sentiment.',
      audio: { src: `${AUDIO_BASE}/sheets-highlight.mp3?${AUDIO_VERSION}`, duration: 9000 },
      cursorPath: [
        { x: -100, y: -60, duration: 500 },
        { x: 0, y: 0, duration: 500 },
        { x: 50, y: 30, duration: 400 },
      ],
    },
    {
      id: 'sheets-decision',
      type: 'decision',
      target: { nodeId: 'sheets' },
      subtitle: '',
      decision: {
        prompt: 'Want to explore the data?',
        options: [
          { label: 'Explore the data', icon: 'search', nextStepId: 'sheets-interact' },
          { label: 'Show me search', icon: 'arrow-right', nextStepId: 'search-highlight' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 1,
      },
    },
    {
      id: 'sheets-interact',
      type: 'interact',
      target: { nodeId: 'sheets' },
      subtitle: 'Browse through the transcribed clips. Click any column header to sort, or filter by speaker or topic.',
      audio: { src: `${AUDIO_BASE}/sheets-interact.mp3?${AUDIO_VERSION}`, duration: 7000 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 5: Full-Text Search ===
    {
      id: 'search-highlight',
      type: 'highlight',
      target: { nodeId: 'search' },
      subtitle: 'Full-text search across every transcript. Find specific phrases, speaker names, or topics across all your videos instantly.',
      audio: { src: `${AUDIO_BASE}/search-highlight.mp3?${AUDIO_VERSION}`, duration: 7500 },
      cursorPath: [
        { x: -60, y: -40, duration: 400 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'search-decision',
      type: 'decision',
      target: { nodeId: 'search' },
      subtitle: '',
      decision: {
        prompt: 'Want to try a search?',
        options: [
          { label: 'Watch demo search', icon: 'play', nextStepId: 'search-demo' },
          { label: 'Try it myself', icon: 'keyboard', nextStepId: 'search-interact' },
        ],
        timeoutMs: 5000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'search-demo',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Let me search for something \u2014 watch this.',
      audio: { src: `${AUDIO_BASE}/search-typing.mp3?${AUDIO_VERSION}`, duration: 2500 },
      interaction: { action: 'type', delay: 100, value: 'product demo' },
      autoAdvanceAfter: 1200,
    },
    // Inject mock search results after typing
    {
      id: 'search-inject-results',
      type: 'inject',
      target: { nodeId: 'search' },
      inject: {
        nodeId: 'search',
        data: {
          demoState: {
            results: DEMO_SEARCH_RESULTS,
            resultCount: 3,
          },
        },
      },
      autoAdvanceAfter: 1200,
    },
    {
      id: 'search-interact',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Type any search term into the search box. Try phrases like "onboarding flow" or "compliance training".',
      audio: { src: `${AUDIO_BASE}/search-interact.mp3?${AUDIO_VERSION}`, duration: 7000 },
      autoAdvanceAfter: 4000,
    },

    // === ENDING ===
    {
      id: 'ending',
      type: 'narrate',
      subtitle: 'That\'s the full video intelligence pipeline \u2014 from raw video files to searchable transcripts. Ready to try it with your own content?',
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 7000 },
    },
    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Start my own flow', icon: 'rocket', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
